import React from 'react';
import styled from 'styled-components';

import { ChildrenType } from 'models/Common/commonTypes';
import { palette, zIndex } from 'styles/Global/globalStyles';
import { Body1Bold, Body3Regular, Body4Regular, Title2Bold } from 'styles/Global/typography';
import { BackgroundColor, TextColor } from 'styles/Global/colorSemantic';
import { ModalPropsWithChildren } from 'models/Common/modalTypes';

export const ModalBackground = ({ children, $type, onClick }: ModalPropsWithChildren) => {
    return (
        <ModalBackgroundWrapper $type={$type} onClick={onClick}>
            {children}
        </ModalBackgroundWrapper>
    );
};

export const ModalLayout = ({ children, $width, $height, $gap, $padding }: ModalPropsWithChildren) => {
    return (
        <ModalLayoutWrapper $width={$width} $height={$height} $gap={$gap} $padding={$padding}>
            {children}
        </ModalLayoutWrapper>
    );
};
export const MobileModalLayout = ({ children, $width, $height, $gap, $padding, $maxWidth }: ModalPropsWithChildren) => {
    return (
        <MobileModalLayoutWrapper $width={$width} $height={$height} $gap={$gap} $padding={$padding} $maxWidth={$maxWidth}>
            {children}
        </MobileModalLayoutWrapper>
    );
};

export const ModalTitle = ({ children }: ModalPropsWithChildren) => {
    return <ModalTitleWrapper>{children}</ModalTitleWrapper>;
};
export const ModalLeftTitle = ({ children }: ModalPropsWithChildren) => {
    return <ModalLeftTitleWrapper>{children}</ModalLeftTitleWrapper>;
};
export const MobileModalLeftTitle = ({ children }: ModalPropsWithChildren) => {
    return <MobileModalLeftTitleWrapper>{children}</MobileModalLeftTitleWrapper>;
};

export const MobileModalCenterTitle = ({ children }: ModalPropsWithChildren) => {
    return <MobileModalCenterTitleWrapper>{children}</MobileModalCenterTitleWrapper>;
};

export const ModalTitleAndContent = ({ children }: ModalPropsWithChildren) => {
    return <ModalTitleAndContentContainer>{children}</ModalTitleAndContentContainer>;
};

export const ModalContent = ({ children }: ModalPropsWithChildren) => {
    return <ModalContentWrapper>{children}</ModalContentWrapper>;
};
export const MobileModalContent = ({ children }: ModalPropsWithChildren) => {
    return <MobileModalContentWrapper>{children}</MobileModalContentWrapper>;
};

export const MobileModalCenterContent = ({ children }: ModalPropsWithChildren) => {
    return <MobileModalCenterContentWrapper>{children}</MobileModalCenterContentWrapper>;
};

export const ModalFooter = ({ $marginTop, children }: ModalPropsWithChildren) => {
    return <ModalFooterWrapper $marginTop={$marginTop}>{children}</ModalFooterWrapper>;
};

export const ModalVerticalFooter = ({ $marginTop, children }: ModalPropsWithChildren) => {
    return <ModalVerticalFooterWrapper $marginTop={$marginTop}>{children}</ModalVerticalFooterWrapper>;
};

export const ModalCenterButtonFooter = ({ children }: ChildrenType) => {
    return <ModalCenterButtonWrapper>{children}</ModalCenterButtonWrapper>;
};

const ModalTitleAndContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 12px;
`;

const ModalBackgroundWrapper = styled.div<{ $type?: string }>`
    display: flex;
    position: fixed;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 0;
    width: 100%;
    min-width: ${(props) => !props.$type && '1280px'};
    height: 100%;
    background-color: ${BackgroundColor.color_background_dim_heavy};
    z-index: ${zIndex.dim};
`;

const ModalLayoutWrapper = styled.section<ModalPropsWithChildren>`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: fixed;
    width: ${(props) => props.$width};
    height: ${(props) => props.$height};
    padding: ${(props) => props.$padding || '40px 36px 32px 36px'};
    border-radius: 12px;
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.2);
    z-index: ${zIndex.modalPopup};
    background-color: ${palette.white};
    gap: ${(props) => props.$gap || '36px'};
`;

const MobileModalLayoutWrapper = styled.section<ModalPropsWithChildren>`
    position: relative;
    display: flex;
    flex-direction: column;
    position: fixed;
    width: ${(props) => props.$width || 'clamp(300px, calc(300px + (100vw - 360px) * 0.25), 360px)'};
    max-width: ${(props) => props.$maxWidth};
    height: ${(props) => props.$height};
    padding: ${(props) => props.$padding || '26px 24px 24px'};
    border-radius: 16px;
    box-shadow: 0px 4px 20px 0px rgba(16, 16, 16, 0.2);
    z-index: ${zIndex.modalPopup};
    background-color: ${palette.white};
    gap: ${(props) => props.$gap || '20px'};
`;

const ModalTitleWrapper = styled(Title2Bold)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;
const ModalLeftTitleWrapper = styled(Title2Bold)`
    display: flex;
    align-items: center;
    width: 100%;
`;
const MobileModalLeftTitleWrapper = styled(Body1Bold)`
    display: flex;
    align-items: center;
    width: 100%;
`;

const MobileModalCenterTitleWrapper = styled(Body1Bold)`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const ModalContentWrapper = styled(Body3Regular)`
    text-align: center;
    width: 410px;
    color: ${TextColor.color_text_neutral};
`;
const MobileModalContentWrapper = styled(Body4Regular)`
    width: 100%;
    color: ${TextColor.color_text_neutral};
`;

const MobileModalCenterContentWrapper = styled(Body4Regular)`
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: pre-line; /* ← 개행문자 \n을 줄바꿈으로 처리 */
    text-align: center; /* 줄바꿈 시 가운데 정렬 */
    color: ${TextColor.color_text_neutral};
`;

const ModalFooterWrapper = styled.div<{ $marginTop?: string }>`
    display: flex;
    gap: 8px;
    margin-top: ${({ $marginTop }) => $marginTop || '0'};
`;

const ModalVerticalFooterWrapper = styled.div<{ $marginTop?: string }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: ${({ $marginTop }) => $marginTop || '0'};
`;

const ModalCenterButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    gap: 10px;
`;
